import React, { useState, useMemo, useCallback } from 'react';
import { theme } from '../../styles/theme';
import { GameState, AgentTurnLog, Card } from '../../types/game';
import { CARD_TEMPLATES, createAgentDeck, shuffleDeck, drawHand, AgentDeckType } from '../../data/cards';
import { tuning } from '../../data/tuning';
import { clearAgentDebugLogs } from '../../engine/agentAI';

interface AgentAIPanelProps {
  gameState: GameState;
  turnLogs: AgentTurnLog[];
  isOpen: boolean;
  onClose: () => void;
  onAgentDeckChange: (newDeck: Card[], newHand: Card[], newDiscard: Card[]) => void;
  onDropSignal?: (lat: number, lng: number, strength: number) => void;
  onResetBelief?: () => void;
  /** Set agent's belief to a specific position with given confidence */
  onSetBelief?: (lat: number, lng: number, confidence: number) => void;
  /** Teleport agent to a specific position */
  onTeleportAgent?: (lat: number, lng: number) => void;
  /** Swap the agent's weapon (replaces the weapon item in equipment) */
  onWeaponChange?: (weaponId: string | null) => void;
}

type PanelSection = 'deck' | 'state' | 'log';
type CardFilter = 'all' | 'movement' | 'combat' | 'intel' | 'tactics' | 'fieldcraft';
type LogFilter = 'all' | 'signals' | 'cards' | 'state' | 'belief';

const CATEGORY_COLORS: Record<string, string> = {
  movement: '#44aaff',
  combat: '#ff3344',
  intel: '#00ff88',
  tactics: '#ffaa44',
  fieldcraft: '#cc66ff',
};

const CATEGORY_SHORT: Record<string, string> = {
  movement: 'MOV',
  combat: 'CMB',
  intel: 'INT',
  tactics: 'TAC',
  fieldcraft: 'FC',
};

export function AgentAIPanel({
  gameState,
  turnLogs,
  isOpen,
  onClose,
  onAgentDeckChange,
  onDropSignal: _onDropSignal,
  onResetBelief,
  onSetBelief,
  onTeleportAgent,
  onWeaponChange,
}: AgentAIPanelProps) {
  const [section, setSection] = useState<PanelSection>('state');
  const [cardFilter, setCardFilter] = useState<CardFilter>('all');
  const [logFilter, setLogFilter] = useState<LogFilter>('all');
  const [expandedTurns, setExpandedTurns] = useState<Set<number>>(new Set());
  const [scenarioConf, setScenarioConf] = useState(0.4);

  const agent = gameState.agent;

  // ── Deck Editor helpers ──

  // Count cards in the full pool (deck + hand + discard)
  const deckCardCounts = useMemo(() => {
    const allCards = [...agent.deck, ...agent.hand, ...agent.discard];
    const counts: Record<string, number> = {};
    for (const card of allCards) {
      const key = Object.entries(CARD_TEMPLATES).find(
        ([, t]) => t.name === card.name
      )?.[0] ?? card.name;
      counts[key] = (counts[key] || 0) + 1;
    }
    return counts;
  }, [agent.deck, agent.hand, agent.discard]);

  const totalDeckCards = useMemo(
    () => agent.deck.length + agent.hand.length + agent.discard.length,
    [agent.deck, agent.hand, agent.discard]
  );

  const availableTemplates = useMemo(() => {
    return Object.entries(CARD_TEMPLATES)
      .filter(([, t]) => {
        if (cardFilter === 'all') return true;
        return t.category === cardFilter;
      })
      .sort((a, b) => a[1].category.localeCompare(b[1].category) || a[1].name.localeCompare(b[1].name));
  }, [cardFilter]);

  // Group current deck by category
  const deckByCategory = useMemo(() => {
    const allCards = [...agent.deck, ...agent.hand, ...agent.discard];
    const groups: Record<string, { key: string; name: string; count: number; category: string }[]> = {};
    const seen = new Map<string, { key: string; name: string; count: number; category: string }>();

    for (const card of allCards) {
      const key = Object.entries(CARD_TEMPLATES).find(
        ([, t]) => t.name === card.name
      )?.[0] ?? card.name;
      if (seen.has(key)) {
        seen.get(key)!.count++;
      } else {
        const entry = { key, name: card.name, count: 1, category: card.category };
        seen.set(key, entry);
      }
    }

    for (const entry of seen.values()) {
      const cat = entry.category;
      if (!groups[cat]) groups[cat] = [];
      groups[cat].push(entry);
    }
    return groups;
  }, [agent.deck, agent.hand, agent.discard]);

  const addCardToDeck = useCallback((templateKey: string) => {
    const template = CARD_TEMPLATES[templateKey];
    if (!template) return;
    const newCard: Card = {
      ...template,
      id: `agent-card-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
    };
    const newDeck = [...agent.deck, newCard];
    onAgentDeckChange(newDeck, agent.hand, agent.discard);
  }, [agent.deck, agent.hand, agent.discard, onAgentDeckChange]);

  const removeCardFromDeck = useCallback((templateKey: string) => {
    const template = CARD_TEMPLATES[templateKey];
    if (!template) return;
    // Remove one copy from deck first, then discard, then hand
    let removed = false;
    const newDeck = [...agent.deck];
    const newHand = [...agent.hand];
    const newDiscard = [...agent.discard];

    for (let i = newDeck.length - 1; i >= 0; i--) {
      if (newDeck[i].name === template.name) {
        newDeck.splice(i, 1);
        removed = true;
        break;
      }
    }
    if (!removed) {
      for (let i = newDiscard.length - 1; i >= 0; i--) {
        if (newDiscard[i].name === template.name) {
          newDiscard.splice(i, 1);
          removed = true;
          break;
        }
      }
    }
    if (!removed) {
      for (let i = newHand.length - 1; i >= 0; i--) {
        if (newHand[i].name === template.name) {
          newHand.splice(i, 1);
          removed = true;
          break;
        }
      }
    }
    if (removed) {
      onAgentDeckChange(newDeck, newHand, newDiscard);
    }
  }, [agent.deck, agent.hand, agent.discard, onAgentDeckChange]);

  const loadPreset = useCallback((archetype: AgentDeckType) => {
    const newDeck = shuffleDeck(createAgentDeck(archetype));
    const { hand, remaining } = drawHand(newDeck, tuning.agentCardSystem.handSize);
    onAgentDeckChange(remaining, hand, []);
  }, [onAgentDeckChange]);

  const reshuffleNow = useCallback(() => {
    const allCards = [...agent.deck, ...agent.hand, ...agent.discard];
    const shuffled = shuffleDeck(allCards);
    const { hand, remaining } = drawHand(shuffled, tuning.agentCardSystem.handSize);
    onAgentDeckChange(remaining, hand, []);
  }, [agent.deck, agent.hand, agent.discard, onAgentDeckChange]);

  // ── Log helpers ──

  const filteredLogs = useMemo(() => {
    if (logFilter === 'all') return turnLogs;
    return turnLogs.filter(log => {
      switch (logFilter) {
        case 'signals': return log.signalsReceived.length > 0;
        case 'cards': return log.cardsPlayed.length > 0;
        case 'state': return log.stateBefore !== log.stateAfter;
        case 'belief': return log.beliefBefore.confidence !== log.beliefAfter.confidence;
        default: return true;
      }
    });
  }, [turnLogs, logFilter]);

  const toggleTurn = useCallback((turn: number) => {
    setExpandedTurns(prev => {
      const next = new Set(prev);
      if (next.has(turn)) next.delete(turn);
      else next.add(turn);
      return next;
    });
  }, []);

  const copyLog = useCallback(() => {
    const text = turnLogs.map(log => formatLogEntry(log)).join('\n\n');
    navigator.clipboard.writeText(text).catch(() => {});
  }, [turnLogs]);

  // ── Render ──

  if (!isOpen) return null;

  return (
    <div style={s.panel}>
      {/* Header */}
      <div style={s.header}>
        <span style={s.headerTitle}>🤖 AGENT AI</span>
        <button style={s.closeButton} onClick={onClose}>✕</button>
      </div>

      {/* Section tabs */}
      <div style={s.tabs}>
        {(['state', 'deck', 'log'] as PanelSection[]).map(tab => (
          <button
            key={tab}
            style={{ ...s.tab, ...(section === tab ? s.tabActive : {}) }}
            onClick={() => setSection(tab)}
          >
            {tab === 'state' ? '📊 STATE' : tab === 'deck' ? '🃏 DECK' : '📜 LOG'}
          </button>
        ))}
      </div>

      <div style={s.content}>
        {/* ═══ STATE SECTION ═══ */}
        {section === 'state' && (
          <>
            {/* Belief */}
            <div style={s.sectionHeader}>BELIEF</div>
            <div style={s.row}>
              <span style={s.label}>Position</span>
              <span style={s.value}>{agent.belief.position.lat.toFixed(4)}, {agent.belief.position.lng.toFixed(4)}</span>
            </div>
            <div style={s.row}>
              <span style={s.label}>Confidence</span>
              <div style={s.barContainer}>
                <div style={{
                  ...s.bar,
                  width: `${Math.min(100, agent.belief.confidence * 100)}%`,
                  background: agent.belief.confidence > 0.7 ? '#ff3344'
                    : agent.belief.confidence > 0.3 ? '#ffaa44' : '#44aaff',
                }} />
                <span style={s.barLabel}>{(agent.belief.confidence * 100).toFixed(0)}%</span>
              </div>
            </div>
            <div style={s.row}>
              <span style={s.label}>Radius</span>
              <span style={s.value}>{Math.round(agent.belief.radius)}m</span>
            </div>
            <div style={s.row}>
              <span style={s.label}>Last Updated</span>
              <span style={s.value}>
                Turn {agent.belief.lastUpdated}
                {agent.belief.lastUpdated > 0 ? ` (${gameState.turn - agent.belief.lastUpdated} ago)` : ''}
              </span>
            </div>
            <div style={s.row}>
              <span style={s.label}>Heading</span>
              <span style={s.value}>
                {agent.belief.inferredHeading !== null
                  ? `${getCompassDir(agent.belief.inferredHeading)} (${Math.round(agent.belief.inferredHeading * 180 / Math.PI)}°)`
                  : '—'}
              </span>
            </div>
            <div style={s.row}>
              <span style={s.label}>Speed Est.</span>
              <span style={s.value}>
                {agent.belief.inferredSpeed !== null
                  ? `~${Math.round(agent.belief.inferredSpeed)}m/turn`
                  : '—'}
              </span>
            </div>
            <div style={s.row}>
              <span style={s.label}>Cleared</span>
              <span style={s.value}>{agent.belief.clearedAreas.length} areas</span>
            </div>

            {/* Current State */}
            <div style={{ ...s.sectionHeader, marginTop: '12px' }}>STATE</div>
            <div style={s.row}>
              <span style={s.label}>Current</span>
              <span style={{
                ...s.value,
                color: agent.state === 'search' ? '#44aaff'
                  : agent.state === 'pursue' ? '#ffaa44' : '#ff3344',
                fontWeight: 700,
              }}>
                {agent.state.toUpperCase()}
              </span>
            </div>
            <div style={s.row}>
              <span style={s.label}>Turns in State</span>
              <span style={s.value}>{agent.turnsInCurrentState}</span>
            </div>
            <div style={s.row}>
              <span style={s.label}>Prep</span>
              <div style={s.barContainer}>
                <div style={{
                  ...s.bar,
                  width: `${Math.min(100, agent.prepBonus * 100)}%`,
                  background: '#ffdd44',
                }} />
                <span style={s.barLabel}>{(agent.prepBonus * 100).toFixed(0)}%</span>
              </div>
            </div>

            {/* Goals */}
            <div style={{ ...s.sectionHeader, marginTop: '12px' }}>GOALS (last turn)</div>
            {turnLogs.length > 0 && (() => {
              const lastLog = turnLogs[turnLogs.length - 1];
              const { goalValues } = lastLog;
              return (
                <>
                  <GoalBar label="Build Prep" value={goalValues.prep} color="#ffdd44" />
                  <GoalBar label="Close Distance" value={goalValues.distance} color="#44aaff" />
                  <GoalBar label="Gather Intel" value={goalValues.intel} color="#00ff88" />
                  <GoalBar label="Set Up Position" value={goalValues.position} color="#ffaa44" />
                </>
              );
            })()}
            {turnLogs.length === 0 && (
              <div style={s.dimText}>No turns played yet</div>
            )}

            {/* Hand & AP */}
            <div style={{ ...s.sectionHeader, marginTop: '12px' }}>HAND & AP</div>
            <div style={s.row}>
              <span style={s.label}>AP</span>
              <span style={s.value}>{agent.ap}/{agent.maxAp}</span>
            </div>
            <div style={s.row}>
              <span style={s.label}>Hand</span>
              <span style={s.value}>{agent.hand.length} cards</span>
            </div>
            <div style={s.handCards}>
              {agent.hand.map(card => (
                <div key={card.id} style={{
                  ...s.handCard,
                  borderColor: CATEGORY_COLORS[card.category] ?? '#666',
                }}>
                  <span style={{ fontSize: '9px', color: CATEGORY_COLORS[card.category] }}>{CATEGORY_SHORT[card.category]}</span>
                  <span style={{ fontSize: '10px' }}>{card.name}</span>
                  <span style={{ fontSize: '9px', color: '#ffcc00' }}>{card.apCost} AP</span>
                </div>
              ))}
              {agent.hand.length === 0 && <div style={s.dimText}>Empty (end of turn)</div>}
            </div>
            <div style={s.row}>
              <span style={s.label}>Deck / Discard</span>
              <span style={s.value}>{agent.deck.length} / {agent.discard.length}</span>
            </div>

            {/* Controls */}
            <div style={{ ...s.sectionHeader, marginTop: '12px' }}>CONTROLS</div>
            <div style={s.controlRow}>
              {onResetBelief && (
                <button style={s.controlButton} onClick={onResetBelief}>🔄 Reset Belief</button>
              )}
              <button style={s.controlButton} onClick={() => {
                console.log('[AgentAI] Full state dump:', JSON.stringify({
                  belief: agent.belief,
                  state: agent.state,
                  deck: agent.deck.map(c => c.name),
                  hand: agent.hand.map(c => c.name),
                  discard: agent.discard.map(c => c.name),
                  prep: agent.prepBonus,
                  ap: agent.ap,
                }, null, 2));
              }}>📊 Dump State</button>
              <button style={{ ...s.controlButton, background: 'rgba(0,200,255,0.15)', borderColor: '#00c8ff' }} onClick={() => {
                // Read directly from window to bypass any module instance issues
                const buf = (window as any).__agentDebugLogs as string[] | undefined;
                const logs = buf && buf.length > 0 ? buf.join('\n') : '';
                const lineCount = buf ? buf.length : 0;
                if (lineCount === 0) {
                  alert(`No debug logs yet — play some turns first.\n\nDebug: window.__agentDebugLogs = ${JSON.stringify(buf)}\nType: ${typeof buf}`);
                  return;
                }
                // Always open in new tab — most reliable
                const w = window.open('', '_blank');
                if (w) {
                  w.document.write(`<pre style="background:#1a1a2e;color:#0f0;padding:16px;font-size:12px;white-space:pre-wrap;word-wrap:break-word">${logs.replace(/</g, '&lt;')}</pre>`);
                  w.document.title = `Agent Debug Logs (${lineCount} lines)`;
                }
                // Also try clipboard
                try {
                  const ta = document.createElement('textarea');
                  ta.value = logs;
                  ta.style.position = 'fixed';
                  ta.style.left = '-9999px';
                  document.body.appendChild(ta);
                  ta.select();
                  document.execCommand('copy');
                  document.body.removeChild(ta);
                } catch {
                  // clipboard failed, but new tab should have it
                }
              }}>📋 Copy Debug Logs</button>
              <button style={{ ...s.controlButton, background: 'rgba(255,100,100,0.15)', borderColor: '#ff6464' }} onClick={() => {
                clearAgentDebugLogs();
                alert('Debug logs cleared');
              }}>🗑️ Clear Logs</button>
            </div>

            {/* Test Scenario Setup */}
            {(onSetBelief || onTeleportAgent) && (
              <>
                <div style={{ ...s.sectionHeader, marginTop: '12px', color: '#ffaa00' }}>TEST SCENARIOS</div>

                {/* Confidence slider */}
                <div style={{ ...s.row, alignItems: 'center', gap: '8px' }}>
                  <span style={s.label}>Confidence</span>
                  <input
                    type="range"
                    min={0.1}
                    max={0.9}
                    step={0.05}
                    value={scenarioConf}
                    onChange={e => setScenarioConf(parseFloat(e.target.value))}
                    style={{ flex: 1, accentColor: '#ffaa00' }}
                  />
                  <span style={{ ...s.value, minWidth: '36px', textAlign: 'right' }}>{scenarioConf.toFixed(2)}</span>
                </div>
                <div style={{ fontSize: '10px', color: '#888', marginBottom: '6px', paddingLeft: '4px' }}>
                  Radius: {Math.round(tuning.agentBelief.baseUncertainty / scenarioConf)}m
                </div>

                <div style={s.controlRow}>
                  {onSetBelief && (
                    <button
                      style={{ ...s.controlButton, background: 'rgba(255,170,0,0.15)', borderColor: '#ffaa00' }}
                      onClick={() => {
                        const p = gameState.player.position;
                        onSetBelief(p.lat, p.lng, scenarioConf);
                      }}
                    >🎯 Belief → Player</button>
                  )}
                  {onSetBelief && onTeleportAgent && (
                    <button
                      style={{ ...s.controlButton, background: 'rgba(255,170,0,0.15)', borderColor: '#ffaa00' }}
                      onClick={() => {
                        // Set belief at player, then teleport agent to edge of belief radius
                        const p = gameState.player.position;
                        const radius = tuning.agentBelief.baseUncertainty / scenarioConf;
                        const offsetLat = (radius * 0.8) / 111000; // ~80% of radius away
                        onSetBelief(p.lat, p.lng, scenarioConf);
                        onTeleportAgent(p.lat + offsetLat, p.lng);
                      }}
                    >🧪 Full Setup</button>
                  )}
                </div>
                <div style={s.controlRow}>
                  {onTeleportAgent && (
                    <button
                      style={{ ...s.controlButton, background: 'rgba(100,200,255,0.15)', borderColor: '#64c8ff' }}
                      onClick={() => {
                        const b = agent.belief.position;
                        onTeleportAgent(b.lat, b.lng);
                      }}
                    >📍 Agent → Belief</button>
                  )}
                  {onTeleportAgent && (
                    <button
                      style={{ ...s.controlButton, background: 'rgba(100,200,255,0.15)', borderColor: '#64c8ff' }}
                      onClick={() => {
                        const b = agent.belief.position;
                        const radius = agent.belief.radius || 200;
                        const angle = Math.random() * 2 * Math.PI;
                        const offsetLat = (radius * 0.8 * Math.cos(angle)) / 111000;
                        const offsetLng = (radius * 0.8 * Math.sin(angle)) / (111000 * Math.cos(b.lat * Math.PI / 180));
                        onTeleportAgent(b.lat + offsetLat, b.lng + offsetLng);
                      }}
                    >📍 Agent → Belief Edge</button>
                  )}
                </div>

                {/* Patrol state info */}
                {agent.beliefSearchCenter && (
                  <div style={{ fontSize: '10px', color: '#ffaa00', marginTop: '6px', padding: '4px', background: 'rgba(255,170,0,0.1)', borderRadius: '4px' }}>
                    Patrol active: {(agent.beliefSearchPath?.length ?? 0)} path pts,
                    radius {Math.round(agent.beliefSearchRadius)}m
                  </div>
                )}

                <div style={{ fontSize: '10px', color: '#666', marginTop: '4px', paddingLeft: '4px' }}>
                  Full Setup: sets belief at player + teleports agent to edge. Then end turns to watch patrol.
                </div>

                {/* Weapon selector */}
                {onWeaponChange && (
                  <>
                    <div style={{ ...s.sectionHeader, marginTop: '12px', color: '#ff5050' }}>AGENT WEAPON</div>
                    <select
                      value={agent.equipment.find(e => e.category === 'weapon')?.id?.replace(/-agent$/, '') ?? 'none'}
                      onChange={e => onWeaponChange(e.target.value === 'none' ? null : e.target.value)}
                      style={{
                        width: '100%',
                        padding: '6px 8px',
                        background: 'rgba(20, 25, 35, 0.9)',
                        border: '1px solid rgba(255, 80, 80, 0.3)',
                        borderRadius: '4px',
                        color: '#ddd',
                        fontFamily: 'monospace',
                        fontSize: '11px',
                        cursor: 'pointer',
                      }}
                    >
                      <option value="none">Unarmed</option>
                      <option value="pistol">Pistol</option>
                      <option value="combat-knife">Combat Knife</option>
                      <option value="sniper-rifle">Sniper Rifle</option>
                    </select>
                  </>
                )}
              </>
            )}
          </>
        )}

        {/* ═══ DECK SECTION ═══ */}
        {section === 'deck' && (
          <>
            <div style={s.row}>
              <span style={s.label}>Total Cards</span>
              <span style={s.value}>{totalDeckCards}</span>
            </div>

            {/* Preset buttons */}
            <div style={s.presetRow}>
              {(['tracker', 'commander', 'ghost', 'sniper'] as AgentDeckType[]).map(arch => (
                <button
                  key={arch}
                  style={{
                    ...s.presetButton,
                    ...(agent.archetype === arch ? { borderColor: '#00ff88', color: '#00ff88' } : {}),
                  }}
                  onClick={() => loadPreset(arch)}
                >
                  {arch.toUpperCase()}
                </button>
              ))}
              <button style={{ ...s.presetButton, borderColor: '#ffaa44', color: '#ffaa44' }} onClick={reshuffleNow}>
                🔄 RESHUFFLE
              </button>
            </div>

            {/* Current deck grouped by category */}
            <div style={{ ...s.sectionHeader, marginTop: '8px' }}>CURRENT DECK</div>
            {Object.entries(deckByCategory).map(([category, entries]) => (
              <div key={category} style={{ marginBottom: '6px' }}>
                <div style={{ fontSize: '9px', color: CATEGORY_COLORS[category] ?? '#666', letterSpacing: '1px', marginBottom: '2px' }}>
                  {category.toUpperCase()}
                </div>
                {entries.map(entry => (
                  <div key={entry.key} style={s.deckCardRow}>
                    <span style={{ fontSize: '11px', flex: 1 }}>{entry.name}</span>
                    <span style={{ fontSize: '10px', color: theme.colors.textDim, marginRight: '6px' }}>×{entry.count}</span>
                    <button style={s.smallButton} onClick={() => removeCardFromDeck(entry.key)}>−</button>
                    <button style={s.smallButton} onClick={() => addCardToDeck(entry.key)}>+</button>
                  </div>
                ))}
              </div>
            ))}

            {/* Add cards */}
            <div style={{ ...s.sectionHeader, marginTop: '12px' }}>ADD CARDS</div>
            <div style={s.filterRow}>
              {(['all', 'movement', 'combat', 'intel', 'tactics', 'fieldcraft'] as CardFilter[]).map(f => (
                <button
                  key={f}
                  style={{ ...s.filterButton, ...(cardFilter === f ? s.filterButtonActive : {}) }}
                  onClick={() => setCardFilter(f)}
                >
                  {f === 'all' ? 'ALL' : CATEGORY_SHORT[f] ?? f.toUpperCase()}
                </button>
              ))}
            </div>
            <div style={s.cardPool}>
              {availableTemplates.map(([key, template]) => (
                <div key={key} style={s.poolCardRow} onClick={() => addCardToDeck(key)}>
                  <span style={{ fontSize: '10px', color: CATEGORY_COLORS[template.category], width: '28px' }}>
                    {CATEGORY_SHORT[template.category]}
                  </span>
                  <span style={{ fontSize: '11px', flex: 1 }}>{template.name}</span>
                  <span style={{ fontSize: '9px', color: '#ffcc00', width: '24px', textAlign: 'right' }}>
                    {template.apCost}AP
                  </span>
                  {deckCardCounts[key] ? (
                    <span style={{ fontSize: '9px', color: theme.colors.textDim, width: '20px', textAlign: 'right' }}>
                      ×{deckCardCounts[key]}
                    </span>
                  ) : null}
                </div>
              ))}
            </div>
          </>
        )}

        {/* ═══ LOG SECTION ═══ */}
        {section === 'log' && (
          <>
            <div style={s.logHeader}>
              <div style={s.filterRow}>
                {(['all', 'signals', 'cards', 'state', 'belief'] as LogFilter[]).map(f => (
                  <button
                    key={f}
                    style={{ ...s.filterButton, ...(logFilter === f ? s.filterButtonActive : {}) }}
                    onClick={() => setLogFilter(f)}
                  >
                    {f.toUpperCase()}
                  </button>
                ))}
              </div>
              <button style={s.controlButton} onClick={copyLog}>📋 COPY</button>
            </div>

            {filteredLogs.length === 0 && (
              <div style={s.dimText}>No turns logged yet</div>
            )}

            <div style={s.logList}>
              {[...filteredLogs].reverse().map(log => {
                const isExpanded = expandedTurns.has(log.turn);
                const stateChanged = log.stateBefore !== log.stateAfter;
                const confDelta = log.beliefAfter.confidence - log.beliefBefore.confidence;

                return (
                  <div key={log.turn} style={s.logEntry}>
                    {/* Collapsed summary */}
                    <div
                      style={{
                        ...s.logSummary,
                        ...(stateChanged ? { borderLeftColor: '#ffaa44' } : {}),
                      }}
                      onClick={() => toggleTurn(log.turn)}
                    >
                      <span style={{ color: theme.colors.textDim, width: '28px' }}>T{log.turn}</span>
                      <span style={{
                        color: log.stateAfter === 'search' ? '#44aaff'
                          : log.stateAfter === 'pursue' ? '#ffaa44' : '#ff3344',
                        width: '56px',
                        fontWeight: 700,
                      }}>
                        {log.stateAfter.toUpperCase()}
                      </span>
                      <span style={{ color: theme.colors.textSecondary, flex: 1, fontSize: '10px' }}>
                        conf={Math.round(log.beliefAfter.confidence * 100)}%
                        {confDelta !== 0 && (
                          <span style={{ color: confDelta > 0 ? '#00ff88' : '#ff3344' }}>
                            {' '}({confDelta > 0 ? '+' : ''}{(confDelta * 100).toFixed(0)}%)
                          </span>
                        )}
                        {log.cardsPlayed.length > 0 && (
                          <span style={{ color: '#00ff88' }}>
                            {' | '}{log.cardsPlayed.map(cp => cp.card.name).join(', ')}
                          </span>
                        )}
                        {log.movementDistance > 0 && (
                          <span style={{ color: '#44aaff' }}>
                            {' | '}{Math.round(log.movementDistance)}m {log.movementDirection}
                          </span>
                        )}
                      </span>
                      <span style={{ color: theme.colors.textDim, fontSize: '10px' }}>
                        {isExpanded ? '▼' : '▶'}
                      </span>
                    </div>

                    {/* Expanded detail */}
                    {isExpanded && (
                      <div style={s.logDetail}>
                        {stateChanged && (
                          <div style={{ color: '#ffaa44', fontSize: '10px', marginBottom: '4px' }}>
                            State: {log.stateBefore.toUpperCase()} → {log.stateAfter.toUpperCase()}
                          </div>
                        )}

                        {log.signalsReceived.length > 0 && (
                          <div style={{ marginBottom: '4px' }}>
                            <div style={{ fontSize: '9px', color: '#00e5ff', letterSpacing: '1px' }}>SIGNALS</div>
                            {log.signalsReceived.map((sr, i) => (
                              <div key={i} style={{ fontSize: '10px', color: theme.colors.textSecondary, paddingLeft: '8px' }}>
                                • {sr.effect} (cred={sr.credibility.toFixed(2)})
                              </div>
                            ))}
                          </div>
                        )}

                        <div style={{ marginBottom: '4px' }}>
                          <div style={{ fontSize: '9px', color: '#00e5ff', letterSpacing: '1px' }}>BELIEF</div>
                          <div style={{ fontSize: '10px', color: theme.colors.textSecondary, paddingLeft: '8px' }}>
                            Conf: {(log.beliefBefore.confidence * 100).toFixed(0)}% → {(log.beliefAfter.confidence * 100).toFixed(0)}%
                            {' | '}Radius: {Math.round(log.beliefAfter.radius)}m
                          </div>
                        </div>

                        <div style={{ marginBottom: '4px' }}>
                          <div style={{ fontSize: '9px', color: '#00e5ff', letterSpacing: '1px' }}>GOALS</div>
                          <div style={{ fontSize: '10px', color: theme.colors.textSecondary, paddingLeft: '8px' }}>
                            prep={log.goalValues.prep.toFixed(2)}
                            {' '}dist={log.goalValues.distance.toFixed(2)}
                            {' '}intel={log.goalValues.intel.toFixed(2)}
                            {' '}pos={log.goalValues.position.toFixed(2)}
                          </div>
                        </div>

                        {log.handDrawn.length > 0 && (
                          <div style={{ marginBottom: '4px' }}>
                            <div style={{ fontSize: '9px', color: '#00e5ff', letterSpacing: '1px' }}>HAND</div>
                            <div style={{ fontSize: '10px', color: theme.colors.textSecondary, paddingLeft: '8px' }}>
                              [{log.handDrawn.map(c => c.name).join(', ')}]
                            </div>
                          </div>
                        )}

                        {log.cardsPlayed.length > 0 && (
                          <div style={{ marginBottom: '4px' }}>
                            <div style={{ fontSize: '9px', color: '#00e5ff', letterSpacing: '1px' }}>CARDS PLAYED</div>
                            {log.cardsPlayed.map((cp, i) => (
                              <div key={i} style={{ fontSize: '10px', color: '#00ff88', paddingLeft: '8px' }}>
                                {i + 1}. {cp.card.name} ({cp.card.apCost} AP, score={cp.score.toFixed(2)})
                                <div style={{ fontSize: '9px', color: theme.colors.textDim, paddingLeft: '12px' }}>
                                  ↳ {cp.effect}
                                </div>
                                <div style={{ fontSize: '9px', color: theme.colors.textDim, paddingLeft: '12px' }}>
                                  {cp.reason}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}

                        {log.movementDistance > 0 && (
                          <div style={{ fontSize: '10px', color: '#44aaff' }}>
                            Movement: {Math.round(log.movementDistance)}m {log.movementDirection}
                          </div>
                        )}

                        {log.detectionResult && (
                          <div style={{ fontSize: '10px', color: '#ff3344', fontWeight: 700 }}>
                            ⚠ VISUAL CONTACT
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ── Sub-components ──

function GoalBar({ label, value, color }: { label: string; value: number; color: string }) {
  return (
    <div style={s.row}>
      <span style={{ ...s.label, flex: '0 0 100px' }}>{label}</span>
      <div style={s.barContainer}>
        <div style={{ ...s.bar, width: `${Math.min(100, value * 100)}%`, background: color }} />
        <span style={s.barLabel}>{value.toFixed(2)}</span>
      </div>
    </div>
  );
}

// ── Helpers ──

function getCompassDir(rad: number): string {
  const deg = ((rad * 180 / Math.PI) + 360) % 360;
  const dirs = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];
  return dirs[Math.round(deg / 45) % 8];
}

function formatLogEntry(log: AgentTurnLog): string {
  const lines = [
    `━━━ TURN ${log.turn} ━━━`,
    `State: ${log.stateBefore.toUpperCase()} → ${log.stateAfter.toUpperCase()} (conf ${(log.beliefAfter.confidence * 100).toFixed(0)}%)`,
    `Belief: (${log.beliefAfter.position.lat.toFixed(4)}, ${log.beliefAfter.position.lng.toFixed(4)}) radius ${Math.round(log.beliefAfter.radius)}m`,
  ];

  if (log.signalsReceived.length > 0) {
    lines.push('Signals:');
    for (const sr of log.signalsReceived) {
      lines.push(`  • ${sr.effect} (cred=${sr.credibility.toFixed(2)})`);
    }
  }

  lines.push(`Goals: prep=${log.goalValues.prep.toFixed(2)} dist=${log.goalValues.distance.toFixed(2)} intel=${log.goalValues.intel.toFixed(2)} pos=${log.goalValues.position.toFixed(2)}`);
  lines.push(`Hand: [${log.handDrawn.map(c => c.name).join(', ')}]`);

  if (log.cardsPlayed.length > 0) {
    lines.push('Cards Played:');
    for (const cp of log.cardsPlayed) {
      lines.push(`  ${cp.card.name} (${cp.card.apCost} AP, score=${cp.score.toFixed(2)}) → ${cp.effect}`);
    }
  }

  if (log.movementDistance > 0) {
    lines.push(`Movement: ${Math.round(log.movementDistance)}m ${log.movementDirection}`);
  }
  if (log.detectionResult) {
    lines.push('⚠ VISUAL CONTACT');
  }

  return lines.join('\n');
}

// ── Styles ──

const s: Record<string, React.CSSProperties> = {
  panel: {
    position: 'fixed',
    top: 0,
    right: 0,
    width: '380px',
    height: '100vh',
    background: 'rgba(8, 12, 20, 0.95)',
    borderLeft: `1px solid ${theme.colors.border}`,
    zIndex: 9998,
    display: 'flex',
    flexDirection: 'column',
    fontFamily: theme.fonts.mono,
    color: theme.colors.textPrimary,
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '8px 12px',
    borderBottom: `1px solid ${theme.colors.border}`,
    background: 'rgba(16, 22, 31, 0.8)',
  },
  headerTitle: {
    fontSize: '12px',
    fontWeight: 700,
    letterSpacing: '1px',
    color: theme.colors.textPrimary,
  },
  closeButton: {
    background: 'none',
    border: 'none',
    color: theme.colors.textDim,
    cursor: 'pointer',
    fontSize: '14px',
    padding: '2px 6px',
  },
  tabs: {
    display: 'flex',
    borderBottom: `1px solid ${theme.colors.border}`,
  },
  tab: {
    flex: 1,
    padding: '6px',
    background: 'none',
    border: 'none',
    borderBottom: '2px solid transparent',
    color: theme.colors.textDim,
    fontSize: '10px',
    fontFamily: theme.fonts.mono,
    letterSpacing: '1px',
    cursor: 'pointer',
  },
  tabActive: {
    color: theme.colors.textPrimary,
    borderBottomColor: '#00ff88',
  },
  content: {
    flex: 1,
    overflowY: 'auto',
    padding: '8px 12px',
  },
  sectionHeader: {
    fontSize: '9px',
    color: theme.colors.textDim,
    letterSpacing: '2px',
    marginBottom: '4px',
    borderBottom: `1px solid ${theme.colors.border}`,
    paddingBottom: '2px',
  },
  row: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '2px 0',
    fontSize: '11px',
  },
  label: {
    color: theme.colors.textSecondary,
    fontSize: '10px',
  },
  value: {
    color: theme.colors.textPrimary,
    fontSize: '11px',
    fontFamily: theme.fonts.mono,
  },
  dimText: {
    color: theme.colors.textDim,
    fontSize: '10px',
    fontStyle: 'italic',
    padding: '4px 0',
  },
  barContainer: {
    flex: 1,
    marginLeft: '8px',
    height: '14px',
    background: 'rgba(30, 42, 58, 0.5)',
    borderRadius: '3px',
    position: 'relative',
    overflow: 'hidden',
  },
  bar: {
    height: '100%',
    borderRadius: '3px',
    transition: 'width 0.3s ease',
    opacity: 0.8,
  },
  barLabel: {
    position: 'absolute',
    right: '4px',
    top: '0',
    fontSize: '9px',
    lineHeight: '14px',
    color: theme.colors.textPrimary,
    fontFamily: theme.fonts.mono,
  },
  handCards: {
    display: 'flex',
    gap: '4px',
    flexWrap: 'wrap',
    padding: '4px 0',
  },
  handCard: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '3px 6px',
    borderRadius: '4px',
    border: '1px solid',
    background: 'rgba(16, 22, 31, 0.6)',
    gap: '1px',
    minWidth: '55px',
  },
  controlRow: {
    display: 'flex',
    gap: '4px',
    flexWrap: 'wrap',
    padding: '4px 0',
  },
  controlButton: {
    background: 'rgba(16, 22, 31, 0.8)',
    border: `1px solid ${theme.colors.border}`,
    borderRadius: '4px',
    color: theme.colors.textSecondary,
    fontSize: '10px',
    fontFamily: theme.fonts.mono,
    padding: '4px 8px',
    cursor: 'pointer',
  },
  presetRow: {
    display: 'flex',
    gap: '4px',
    flexWrap: 'wrap',
    padding: '6px 0',
  },
  presetButton: {
    background: 'rgba(16, 22, 31, 0.8)',
    border: `1px solid ${theme.colors.border}`,
    borderRadius: '4px',
    color: theme.colors.textSecondary,
    fontSize: '9px',
    fontFamily: theme.fonts.mono,
    letterSpacing: '1px',
    padding: '4px 8px',
    cursor: 'pointer',
  },
  deckCardRow: {
    display: 'flex',
    alignItems: 'center',
    padding: '2px 4px',
    color: theme.colors.textPrimary,
  },
  smallButton: {
    background: 'rgba(16, 22, 31, 0.8)',
    border: `1px solid ${theme.colors.border}`,
    borderRadius: '3px',
    color: theme.colors.textSecondary,
    fontSize: '12px',
    fontFamily: theme.fonts.mono,
    width: '20px',
    height: '20px',
    cursor: 'pointer',
    marginLeft: '2px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 0,
  },
  filterRow: {
    display: 'flex',
    gap: '2px',
    padding: '4px 0',
  },
  filterButton: {
    background: 'none',
    border: `1px solid ${theme.colors.border}`,
    borderRadius: '3px',
    color: theme.colors.textDim,
    fontSize: '9px',
    fontFamily: theme.fonts.mono,
    padding: '2px 6px',
    cursor: 'pointer',
  },
  filterButtonActive: {
    borderColor: '#00ff88',
    color: '#00ff88',
  },
  cardPool: {
    maxHeight: '200px',
    overflowY: 'auto',
    border: `1px solid ${theme.colors.border}`,
    borderRadius: '4px',
    padding: '2px',
  },
  poolCardRow: {
    display: 'flex',
    alignItems: 'center',
    padding: '3px 4px',
    cursor: 'pointer',
    color: theme.colors.textPrimary,
    fontSize: '11px',
    borderRadius: '2px',
  },
  logHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '4px',
  },
  logList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  logEntry: {
    borderRadius: '4px',
    overflow: 'hidden',
  },
  logSummary: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    padding: '4px 6px',
    background: 'rgba(16, 22, 31, 0.6)',
    borderLeft: '3px solid transparent',
    cursor: 'pointer',
    fontSize: '11px',
    fontFamily: theme.fonts.mono,
  },
  logDetail: {
    padding: '6px 8px 6px 16px',
    background: 'rgba(10, 14, 20, 0.8)',
    borderLeft: `3px solid ${theme.colors.border}`,
    fontFamily: theme.fonts.mono,
  },
};
